import { centroid } from "@turf/turf";
import L from "leaflet";
import { toastr } from "react-redux-toastr";

import { isPointInPolygon } from "helpers/isPointInPolygon";
import {
  createContext,
  RefObject,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState
} from "react";
import { useMapContext } from "./MapContextProvider";

const CROP_SIZE = 800;

const LabelingContext = createContext(null);

function useZoomWatcher({ mapRef }) {
  const [zoom, setZoom] = useState(null);

  useLayoutEffect(() => {
    let intervalId;
    let cleanup = () => {};
    const trySetupListener = () => {
      const map = mapRef?.current;
      if (!map) return;
      setZoom(map.getZoom());
      const handleZoomEnd = () => {
        const newZoom = map.getZoom();
        setZoom(newZoom);
      };
      map.on("zoomend", handleZoomEnd);
      cleanup = () => {
        map.off("zoomend", handleZoomEnd);
      };
      clearInterval(intervalId);
    };
    intervalId = setInterval(trySetupListener, 200);
    return () => {
      cleanup();
    };
  }, [mapRef]);

  return zoom;
}

function useImageTransform(
  cropCenter: [number, number] | null,
  mapRef: RefObject<L.Map>
) {
  return useMemo(() => {
    if (!cropCenter || !mapRef.current) return null;
    const map = mapRef.current;

    const imgDom = document.querySelector(
      ".leaflet-image-layer"
    ) as HTMLImageElement;
    if (!imgDom) return null;
    const originalSrc = imgDom.src;

    const transform =
      imgDom.style.transform || getComputedStyle(imgDom).transform;

    let angleRad = 0;
    const matchDeg = /rotate\(([-\d.]+)deg\)/.exec(transform);
    const matchRad = /rotate\(([-\d.]+)rad\)/.exec(transform);
    if (matchDeg) angleRad = (parseFloat(matchDeg[1]) * Math.PI) / 180;
    else if (matchRad) angleRad = parseFloat(matchRad[1]);
    else if (transform.startsWith("matrix")) {
      const m = transform.match(/matrix\(([^)]+)\)/);
      if (m) {
        const [a, b] = m[1].split(",").map((s) => Number(s.trim()));
        angleRad = Math.atan2(b, a);
      }
    }

    const cornersAttr = imgDom.getAttribute("data-corners");
    if (!cornersAttr) return null;

    const mapCorners = JSON.parse(cornersAttr);
    const layerCorners = mapCorners.map((latlng: [number, number]) =>
      map.latLngToLayerPoint(latlng)
    );
    const [tl, tr, br] = layerCorners;
    const origWidth = imgDom.naturalWidth;
    const origHeight = imgDom.naturalHeight;

    const vx: [number, number] = [tr.x - tl.x, tr.y - tl.y];
    const vy: [number, number] = [br.x - tr.x, br.y - tr.y];

    const centerPoint = map.latLngToLayerPoint(cropCenter);
    const vxLen = Math.hypot(vx[0], vx[1]);
    const vyLen = Math.hypot(vy[0], vy[1]);
    const vCenter = [centerPoint.x - tl.x, centerPoint.y - tl.y];
    const projX = (vCenter[0] * vx[0] + vCenter[1] * vx[1]) / vxLen ** 2;
    const projY = (vCenter[0] * vy[0] + vCenter[1] * vy[1]) / vyLen ** 2;
    const origCenterX = projX * origWidth;
    const origCenterY = projY * origHeight;

    const layerToImage = (pt: L.Point): [number, number] => {
      const det = vx[0] * vy[1] - vx[1] * vy[0];
      const px = ((pt.x - tl.x) * vy[1] - (pt.y - tl.y) * vy[0]) / det;
      const py = (-(pt.x - tl.x) * vx[1] + (pt.y - tl.y) * vx[0]) / det;
      return [px * origWidth, py * origHeight];
    };

    const imageToLayer = ([x, y]: [number, number]): L.Point => {
      const px = x / origWidth;
      const py = y / origHeight;
      return L.point(
        tl.x + vx[0] * px + vy[0] * py,
        tl.y + vx[1] * px + vy[1] * py
      );
    };

    return {
      originalSrc,
      angleRad,
      origWidth,
      origHeight,
      origCenterX,
      origCenterY,
      layerToImage,
      imageToLayer,
      map,
      vx,
      vy,
      tl,
      geoCorners: mapCorners
    };
  }, [cropCenter, mapRef]);
}

export function rotate(
  [x, y]: [number, number],
  [cx, cy]: [number, number],
  angleRad: number
): [number, number] {
  const cos = Math.cos(angleRad);
  const sin = Math.sin(angleRad);
  const dx = x - cx;
  const dy = y - cy;
  return [cx + dx * cos - dy * sin, cy + dx * sin + dy * cos];
}

export const LabelingContextProvider = ({ children }) => {
  const { mapRef } = useMapContext();
  const zoom = useZoomWatcher({ mapRef });

  const [labelingPolygon, setLabelingPolygon] = useState<[number, number][]>(
    []
  );

  const cropCenter: [number, number] = useMemo(() => {
    const points = labelingPolygon;
    if (!points || points.length === 0) return null;
    if (points.length < 3) return points[0];

    const turfPoly = {
      type: "Feature",
      geometry: {
        type: "Polygon",
        coordinates: [points]
      },
      properties: {}
    };
    const c = centroid(turfPoly as any).geometry.coordinates;
    return [c[0], c[1]];
  }, [labelingPolygon, zoom]);

  const cropTransform = useImageTransform(cropCenter, mapRef);

  const createRectangleFromDiagonal = useCallback(
    (
      pointA: [number, number], // [lat, lng]
      pointC: [number, number]
    ): [number, number][] | null => {
      if (!mapRef.current || !cropTransform) return null;
      const { layerToImage, imageToLayer, map, origWidth, origHeight } =
        cropTransform;

      const aImg = layerToImage(map.latLngToLayerPoint(pointA));
      const cImg = layerToImage(map.latLngToLayerPoint(pointC));

      let minX = Math.min(aImg[0], cImg[0]);
      const isMinXA = aImg[0] < cImg[0];
      let maxX = Math.max(aImg[0], cImg[0]);
      let minY = Math.min(aImg[1], cImg[1]);
      const isMinYA = aImg[1] < cImg[1];
      let maxY = Math.max(aImg[1], cImg[1]);

      if (maxX - minX > CROP_SIZE) {
        const difference = maxX - minX - CROP_SIZE;
        if (isMinXA) {
          minX = minX + difference;
        } else {
          maxX = maxX - difference;
        }
      }
      if (maxY - minY > CROP_SIZE) {
        const difference = maxY - minY - CROP_SIZE;
        if (isMinYA) {
          minY = minY + difference;
        } else {
          maxY = maxY - difference;
        }
      }

      if (minX < 0) {
        minX = 0;
      }
      if (maxX > origWidth) {
        const difference = maxX - origWidth;
        maxX = maxX - difference;
      }

      if (minY < 0) {
        minY = 0;
      }
      if (maxY > origHeight) {
        const difference = maxY - origHeight;
        maxY = maxY - difference;
      }

      const rectImg: [number, number][] = [
        [minX, minY],
        [maxX, minY],
        [maxX, maxY],
        [minX, maxY],
        [minX, minY]
      ];

      return rectImg.map(imageToLayer).map((pt) => {
        const latlng = map.layerPointToLatLng(pt);
        return [latlng.lat, latlng.lng] as [number, number];
      });
    },
    [mapRef, cropTransform]
  );

  const cropPolygon = useMemo(() => {
    if (!cropTransform) return null;
    const {
      // angleRad,
      origWidth,
      origHeight,
      origCenterX,
      origCenterY,
      vx,
      vy,
      tl,
      map
    } = cropTransform;

    // const half = CROP_SIZE / 2;
    // const cropCornersOrig = [
    //   [origCenterX - half, origCenterY - half],
    //   [origCenterX + half, origCenterY - half],
    //   [origCenterX + half, origCenterY + half],
    //   [origCenterX - half, origCenterY + half]
    // ];
    const half = CROP_SIZE / 2;

    // Обмеження центру, щоб crop не вилізав
    const fixedCenterX = Math.min(
      Math.max(origCenterX, half),
      origWidth - half
    );
    const fixedCenterY = Math.min(
      Math.max(origCenterY, half),
      origHeight - half
    );

    const cropCornersOrig = [
      [fixedCenterX - half, fixedCenterY - half],
      [fixedCenterX + half, fixedCenterY - half],
      [fixedCenterX + half, fixedCenterY + half],
      [fixedCenterX - half, fixedCenterY + half]
    ];

    // Restore if you need a rotation
    // const cropCornersRotated = cropCornersOrig.map((pt) =>
    //   // rotate([pt[0], pt[1]], [origCenterX, origCenterY], -angleRad)
    // );
    // const cropCornersLayer = cropCornersRotated.map(([x, y]) => {

    const cropCornersLayer = cropCornersOrig.map(([x, y]) => {
      const px = x / origWidth;
      const py = y / origHeight;
      return L.point(
        tl.x + vx[0] * px + vy[0] * py,
        tl.y + vx[1] * px + vy[1] * py
      );
    });
    return cropCornersLayer.map((pt) => map.layerPointToLatLng(pt));
  }, [cropTransform]);

  const convertToGeoPolygon = useCallback(
    (coordinates) =>
      coordinates && coordinates.length > 0
        ? {
            type: "Polygon" as const,
            coordinates: [
              [
                ...coordinates.map(
                  (pt) =>
                    Array.isArray(pt)
                      ? [pt[1], pt[0]] // якщо [lat, lng]
                      : [pt.lng, pt.lat] // якщо L.LatLng
                ),
                // замкнути полігон
                Array.isArray(coordinates[0])
                  ? [coordinates[0][1], coordinates[0][0]]
                  : [coordinates[0].lng, coordinates[0].lat]
              ]
            ]
          }
        : null,
    []
  );

  const addPolygonPoint = useCallback(
    (coordinate: [number, number]) => {
      if (!cropPolygon) {
        setLabelingPolygon([coordinate]);
        return;
      }

      if (labelingPolygon.length > 4) return;

      if (
        !isPointInPolygon(
          [coordinate[1], coordinate[0]],
          convertToGeoPolygon(cropPolygon)
        )
      ) {
        toastr.error(`Error`, `Please select point inside crop area`);

        return;
      }

      const polygonFeature = createRectangleFromDiagonal(
        labelingPolygon[0],
        coordinate
      );
      setLabelingPolygon(polygonFeature);
    },
    [
      convertToGeoPolygon,
      createRectangleFromDiagonal,
      cropPolygon,
      labelingPolygon
    ]
  );

  const updateCornerPosition = useCallback(
    (index: number, coordinate: [number, number]) => {
      // if (
      //   !isPointInPolygon(
      //     [coordinate[1], coordinate[0]],
      //     convertToGeoPolygon(cropPolygon)
      //   )
      // ) {
      //   // toastr.error(`Error`, `Please select point inside crop area`);

      //   return;
      // }
      setLabelingPolygon((prev) => {
        const points = prev.slice(0, 4);
        if (points.length === 4) {
          const oppositeIndex = (index + 2) % 4;
          const diagonalPoint = points[oppositeIndex];
          const newPolygon = createRectangleFromDiagonal(
            coordinate,
            diagonalPoint
          );
          return newPolygon ?? prev;
        } else {
          return prev.map((point, i) => (i === index ? coordinate : point));
        }
      });
    },
    [createRectangleFromDiagonal]
  );

  const updateLabelPolygonPosition = useCallback(
    (polygon) => {
      setLabelingPolygon((prev) => {
        const newPolygon = createRectangleFromDiagonal(polygon[0], polygon[2]);
        return newPolygon ?? prev;
      });
    },
    [createRectangleFromDiagonal]
  );

  const clearPolygonPoints = useCallback(() => {
    setLabelingPolygon([]);
  }, []);

  const generateLabelingData = useCallback(async () => {
    if (
      !cropTransform ||
      !cropPolygon ||
      cropPolygon.length !== 4 ||
      labelingPolygon?.length === 0
    ) {
      return undefined;
    }

    const { map, layerToImage, originalSrc } = cropTransform;

    const cropPolygonImg = cropPolygon.map((pt) =>
      layerToImage(map.latLngToLayerPoint(pt))
    );

    const cropXs = cropPolygonImg.map(([x]) => x);
    const cropYs = cropPolygonImg.map(([, y]) => y);
    const cropLeft = Math.min(...cropXs);
    const cropTop = Math.min(...cropYs);

    const base64: string = await new Promise((resolve) => {
      const img = new window.Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        const tempCanvas = document.createElement("canvas");
        tempCanvas.width = img.width;
        tempCanvas.height = img.height;

        const tempCtx = tempCanvas.getContext("2d");
        if (!tempCtx) return resolve("");

        tempCtx.drawImage(img, 0, 0);

        const cropCanvas = document.createElement("canvas");
        cropCanvas.width = CROP_SIZE;
        cropCanvas.height = CROP_SIZE;
        const cropCtx = cropCanvas.getContext("2d");
        if (!cropCtx) return resolve("");

        cropCtx.drawImage(
          tempCanvas,
          cropLeft,
          cropTop,
          CROP_SIZE,
          CROP_SIZE,
          0,
          0,
          CROP_SIZE,
          CROP_SIZE
        );

        resolve(cropCanvas.toDataURL("image/png"));
      };
      img.src = originalSrc;
    });

    const labelingPolygonImgCoords = labelingPolygon
      .slice(0, -1)
      .map((pt) => layerToImage(map.latLngToLayerPoint(pt)));

    const labelingBox = labelingPolygonImgCoords.map(([x, y]) => [
      (x - cropLeft) / CROP_SIZE,
      (y - cropTop) / CROP_SIZE
    ]);

    const xs = labelingBox.map(([x]) => x);
    const ys = labelingBox.map(([, y]) => y);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);

    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;
    const width = maxX - minX;
    const height = maxY - minY;

    const bbox: [number, number, number, number] = [
      centerX,
      centerY,
      width,
      height
    ];

    const point = (() => {
      const coords = labelingPolygon.slice(0, -1);
      const sum = coords.reduce(
        (acc, [lat, lng]) => {
          acc.lat += lat;
          acc.lng += lng;
          return acc;
        },
        { lat: 0, lng: 0 }
      );

      return {
        lat: sum.lat / coords.length,
        lng: sum.lng / coords.length
      };
    })();

    return {
      base64,
      bbox,
      point
    };
  }, [cropTransform, cropPolygon, labelingPolygon]);

  return (
    <LabelingContext.Provider
      value={{
        labelingPolygon,
        addPolygonPoint,
        updateCornerPosition,
        updateLabelPolygonPosition,
        clearPolygonPoints,
        cropPolygon,
        cropCenter,
        cropTransform,
        generateLabelingData
      }}
    >
      {children}
    </LabelingContext.Provider>
  );
};

export const useLabelingContext = () => {
  const context = useContext(LabelingContext);
  if (!context) {
    throw new Error(
      "useLabelingContext must be used within a LabelingContextProvider"
    );
  }
  return context;
};
